import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/VisualPromptEditor.css';

function VisualPromptEditor() {
  const [prompt, setPrompt] = useState('');
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor', 'templates', 'saved'
  const [promptSections, setPromptSections] = useState([
    { id: 1, title: 'Context', content: '', isExpanded: true },
    { id: 2, title: 'Instructions', content: '', isExpanded: true },
    { id: 3, title: 'Examples', content: '', isExpanded: true }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePromptId, setActivePromptId] = useState(null);
  const [promptTemplates, setPromptTemplates] = useState([
    { id: 'template1', name: 'General Q&A', description: 'Basic prompt for general question answering', sections: [] },
    { id: 'template2', name: 'Document Summary', description: 'Summarize a document with key points', sections: [] },
    { id: 'template3', name: 'Code Generation', description: 'Generate code based on requirements', sections: [] }
  ]);
  const [showPreview, setShowPreview] = useState(false);

  // Handle loading saved prompts
  useEffect(() => {
    loadSavedPrompts();
    loadActivePromptId();
  }, []);

  // Update the combined prompt when sections change
  useEffect(() => {
    const fullPrompt = promptSections
      .map(section => `## ${section.title}\n${section.content}`)
      .join('\n\n');
    setPrompt(fullPrompt);
  }, [promptSections]);

  const loadActivePromptId = async () => {
    try {
      const response = await apiService.getActivePromptId();
      setActivePromptId(response.active_prompt_id);
    } catch (error) {
      console.error('Error loading active prompt ID:', error);
    }
  };

  const loadSavedPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Real API call instead of mock data
      const data = await apiService.getSavedPrompts();
      setSavedPrompts(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading saved prompts:', error);
      setError('Failed to load saved prompts');
      setIsLoading(false);
    }
  };

  const handleSectionContentChange = (id, content) => {
    setPromptSections(prevSections => 
      prevSections.map(section => 
        section.id === id ? { ...section, content } : section
      )
    );
  };

  const handleSectionToggle = (id) => {
    setPromptSections(prevSections => 
      prevSections.map(section => 
        section.id === id ? { ...section, isExpanded: !section.isExpanded } : section
      )
    );
  };

  const handleAddSection = () => {
    const newId = Math.max(...promptSections.map(s => s.id)) + 1;
    setPromptSections([
      ...promptSections,
      { id: newId, title: 'New Section', content: '', isExpanded: true }
    ]);
  };

  const handleRemoveSection = (id) => {
    if (promptSections.length <= 1) {
      setError('Cannot remove the last section');
      return;
    }
    setPromptSections(promptSections.filter(section => section.id !== id));
  };

  const handleSectionTitleChange = (id, title) => {
    setPromptSections(prevSections => 
      prevSections.map(section => 
        section.id === id ? { ...section, title } : section
      )
    );
  };

  const handleMoveSection = (id, direction) => {
    const sectionIndex = promptSections.findIndex(section => section.id === id);
    if (
      (direction === 'up' && sectionIndex === 0) || 
      (direction === 'down' && sectionIndex === promptSections.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
    const newSections = [...promptSections];
    const sectionToMove = newSections[sectionIndex];
    newSections.splice(sectionIndex, 1);
    newSections.splice(newIndex, 0, sectionToMove);
    setPromptSections(newSections);
  };

  const handleSavePrompt = async () => {
    if (!promptName.trim()) {
      setError('Please provide a name for your prompt');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const promptData = {
        name: promptName,
        description: promptDescription,
        content: prompt,
        sections: promptSections
      };

      // Use the real API endpoint
      const savedPrompt = await apiService.savePrompt(promptData);
      
      // Update the saved prompts list
      setSavedPrompts([...savedPrompts, savedPrompt]);

      setIsLoading(false);
      // Clear form or go to saved prompts
      setActiveTab('saved');
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError('Failed to save prompt');
      setIsLoading(false);
    }
  };

  const handleUsePrompt = async (promptId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Call the API to use this prompt as the active QA_PROMPT
      await apiService.usePrompt(promptId);
      
      // Update the active prompt ID
      setActivePromptId(promptId);
      
      setIsLoading(false);
      setError(null);
      
      // Show a success message (optional)
      alert('Prompt is now active and will be used for QA responses');
    } catch (error) {
      console.error('Error using prompt:', error);
      setError('Failed to apply prompt');
      setIsLoading(false);
    }
  };

  const handleDeletePrompt = async (promptId) => {
    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this prompt?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Call the API to delete the prompt
      await apiService.deletePrompt(promptId);
      
      // Update the local state to remove the deleted prompt
      setSavedPrompts(savedPrompts.filter(prompt => prompt.id !== promptId));
      
      // If the active prompt was deleted, reset the active prompt ID
      if (activePromptId === promptId) {
        setActivePromptId(null);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setError('Failed to delete prompt');
      setIsLoading(false);
    }
  };

  const handleLoadPrompt = async (promptId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the prompt details from the API
      const promptData = await apiService.getPrompt(promptId);
      
      // Set the editor fields
      setPromptName(promptData.name);
      setPromptDescription(promptData.description);
      
      // Set the sections
      setPromptSections(promptData.sections);
      
      setIsLoading(false);
      // Switch to editor tab
      setActiveTab('editor');
    } catch (error) {
      console.error('Error loading prompt:', error);
      setError('Failed to load prompt');
      setIsLoading(false);
    }
  };

  const handleUseTemplate = (templateId) => {
    // Find the template
    const template = promptTemplates.find(t => t.id === templateId);
    if (!template) return;

    // In a real app, this would populate with actual template data
    // For demo, just set the name and description and switch to editor tab
    setPromptName(template.name);
    setPromptDescription(template.description);
    
    // Set template-specific sections based on the template ID
    if (templateId === 'template1') {
      setPromptSections([
        { id: 1, title: 'Context', content: 'You are a helpful assistant answering questions from users.', isExpanded: true },
        { id: 2, title: 'Instructions', content: 'Provide accurate, concise answers to user questions.', isExpanded: true },
        { id: 3, title: 'Format', content: 'Your responses should be well-structured with clear explanations.', isExpanded: true }
      ]);
    } else if (templateId === 'template2') {
      setPromptSections([
        { id: 1, title: 'Context', content: 'You are summarizing a document for a busy professional.', isExpanded: true },
        { id: 2, title: 'Instructions', content: 'Extract the main points and key details from the document.', isExpanded: true },
        { id: 3, title: 'Format', content: 'Present the summary in bullet points with a brief introduction.', isExpanded: true }
      ]);
    } else if (templateId === 'template3') {
      setPromptSections([
        { id: 1, title: 'Context', content: 'You are a code generation assistant helping developers.', isExpanded: true },
        { id: 2, title: 'Instructions', content: 'Generate clean, efficient, and well-commented code based on the requirements.', isExpanded: true },
        { id: 3, title: 'Constraints', content: 'Follow best practices and provide explanations for complex sections.', isExpanded: true },
        { id: 4, title: 'Examples', content: '# Example request\nCreate a function that sorts an array\n\n# Example response\n```javascript\nfunction sortArray(arr) {\n  // Sort the array in ascending order\n  return [...arr].sort((a, b) => a - b);\n}\n```', isExpanded: true }
      ]);
    }
    
    setActiveTab('editor');
  };

  const renderEditorTab = () => (
    <div className="prompt-editor">
      <div className="form-group">
        <label htmlFor="promptName">Prompt Name:</label>
        <input
          id="promptName"
          type="text"
          value={promptName}
          onChange={(e) => setPromptName(e.target.value)}
          placeholder="Enter a name for your prompt"
          className="form-control"
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="promptDescription">Description:</label>
        <input
          id="promptDescription"
          type="text"
          value={promptDescription}
          onChange={(e) => setPromptDescription(e.target.value)}
          placeholder="Enter a brief description"
          className="form-control"
        />
      </div>
      
      <div className="prompt-sections">
        {promptSections.map((section, index) => (
          <div key={section.id} className="prompt-section">
            <div className="section-header">
              <div className="section-title-container">
                <button 
                  className="section-toggle-btn"
                  onClick={() => handleSectionToggle(section.id)}
                >
                  <span className="material-icons">
                    {section.isExpanded ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => handleSectionTitleChange(section.id, e.target.value)}
                  className="section-title-input"
                />
              </div>
              <div className="section-actions">
                <button 
                  className="section-move-btn"
                  onClick={() => handleMoveSection(section.id, 'up')}
                  disabled={index === 0}
                  title="Move up"
                >
                  <span className="material-icons">arrow_upward</span>
                </button>
                <button 
                  className="section-move-btn"
                  onClick={() => handleMoveSection(section.id, 'down')}
                  disabled={index === promptSections.length - 1}
                  title="Move down"
                >
                  <span className="material-icons">arrow_downward</span>
                </button>
                <button 
                  className="section-remove-btn"
                  onClick={() => handleRemoveSection(section.id)}
                  title="Remove section"
                >
                  <span className="material-icons">delete</span>
                </button>
              </div>
            </div>
            
            {section.isExpanded && (
              <textarea
                value={section.content}
                onChange={(e) => handleSectionContentChange(section.id, e.target.value)}
                placeholder={`Enter content for ${section.title} section...`}
                className="form-control section-content"
                rows={5}
              />
            )}
          </div>
        ))}
      </div>
      
      <div className="prompt-editor-actions">
        <div className="left-actions">
          <button 
            className="add-section-btn"
            onClick={handleAddSection}
          >
            <span className="material-icons">add</span>
            Add Section
          </button>
          
          <button 
            className="preview-prompt-btn"
            onClick={() => setShowPreview(!showPreview)}
          >
            <span className="material-icons">{showPreview ? 'visibility_off' : 'visibility'}</span>
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
        
        <button 
          className="save-prompt-btn"
          onClick={handleSavePrompt}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Prompt'}
        </button>
      </div>
      
      {showPreview && (
        <div className="prompt-preview">
          <h3>
            <span className="material-icons">description</span>
            Prompt Preview
          </h3>
          <pre className="preview-content">{prompt}</pre>
        </div>
      )}
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="templates-container">
      <h3>Available Templates</h3>
      <div className="template-grid">
        {promptTemplates.map((template) => (
          <div key={template.id} className="template-card">
            <h4>{template.name}</h4>
            <p>{template.description}</p>
            <button 
              className="use-template-btn"
              onClick={() => handleUseTemplate(template.id)}
            >
              Use Template
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSavedPromptsTab = () => (
    <div className="saved-prompts-container">
      <h3>Your Saved Prompts</h3>
      
      {savedPrompts.length === 0 ? (
        <div className="no-prompts">
          <p>You don't have any saved prompts yet.</p>
          <button 
            className="create-prompt-btn"
            onClick={() => setActiveTab('editor')}
          >
            Create Your First Prompt
          </button>
        </div>
      ) : (
        <div className="saved-prompts-grid">
          {savedPrompts.map((savedPrompt) => (
            <div key={savedPrompt.id} className="saved-prompt-card">
              <h4>{savedPrompt.name}</h4>
              <p>{savedPrompt.description}</p>
              {activePromptId === savedPrompt.id && (
                <div className="active-prompt-indicator">
                  <span className="material-icons">check_circle</span>
                  Active
                </div>
              )}
              <div className="saved-prompt-actions">
                <button 
                  className="edit-prompt-btn"
                  onClick={() => handleLoadPrompt(savedPrompt.id)}
                >
                  <span className="material-icons">edit</span>
                  Edit
                </button>
                <button 
                  className="use-prompt-btn"
                  onClick={() => handleUsePrompt(savedPrompt.id)}
                  disabled={isLoading || activePromptId === savedPrompt.id}
                >
                  <span className="material-icons">play_arrow</span>
                  Use
                </button>
                <button 
                  className="delete-prompt-btn"
                  onClick={() => handleDeletePrompt(savedPrompt.id)}
                  disabled={isLoading}
                >
                  <span className="material-icons">delete</span>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="visual-prompt-editor-container">
     
      
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          Editor
        </button>
        <button 
          className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
        <button 
          className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved')}
        >
          Saved Prompts
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'editor' && renderEditorTab()}
        {activeTab === 'templates' && renderTemplatesTab()}
        {activeTab === 'saved' && renderSavedPromptsTab()}
      </div>
    </div>
  );
}

export default VisualPromptEditor;